// src/services/authService.js
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword 
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Authentication service for Firebase
 * Handles user login, logout, and authentication state
 */

export const authService = {
  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    try {
      if (!auth) {
        throw new Error('Firebase auth not initialized');
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return {
        success: true,
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName
        }
      };
    } catch (error) {
      console.error('Sign in error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      if (!auth) {
        throw new Error('Firebase auth not initialized');
      }
      
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Create new user account
   */
  async createUser(email, password) {
    try {
      if (!auth) {
        throw new Error('Firebase auth not initialized');
      }
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return {
        success: true,
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email
        }
      };
    } catch (error) {
      console.error('Create user error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  /**
   * Listen to authentication state changes
   */
  onAuthStateChanged(callback) {
    if (!auth) {
      callback(null);
      return () => {};
    }
    
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        callback({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        });
      } else {
        callback(null);
      }
    });
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    if (!auth || !auth.currentUser) {
      return null;
    }
    
    return {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      displayName: auth.currentUser.displayName
    };
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return auth && auth.currentUser !== null;
  },

  /**
   * Get user-friendly error messages
   */
  getErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No user found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-disabled':
        return 'This user account has been disabled.';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please try again later.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      default:
        return 'An error occurred during authentication.';
    }
  }
};

export default authService;
